import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';

export type PlanType = 'gratuito' | 'basico' | 'essencial' | 'empresarial';
export type PlanStatus = 'ativo' | 'expirado' | 'cancelado';

export interface UserPlan {
  id: string;
  user_id: string;
  plan_type: PlanType;
  start_date: string;
  end_date: string | null;
  status: PlanStatus;
  created_at: string;
  updated_at: string;
}

export const useUserPlan = () => {
  const { user } = useAuth();
  const [plan, setPlan] = useState<UserPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserPlan = async () => {
      if (!user) {
        setPlan(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Usar a função get_current_plan do Supabase
        const { data, error: rpcError } = await supabase.rpc('get_current_plan', {
          p_user_id: user.id
        });

        if (rpcError) {
          console.error('Error fetching user plan:', rpcError);
          setError(rpcError.message);
          
          // Fallback: tentar buscar diretamente da tabela
          const { data: planData, error: selectError } = await supabase
            .from('user_plans')
            .select('*')
            .eq('user_id', user.id)
            .eq('status', 'ativo')
            .order('start_date', { ascending: false })
            .limit(1)
            .single();

          if (selectError) {
            console.error('Error with fallback query:', selectError);
            setError(selectError.message);
          } else {
            setPlan(planData as UserPlan);
          }
        } else if (data && data.length > 0) {
          setPlan(data[0] as UserPlan);
        } else {
          // Se não há plano, pode ser que o usuário seja novo e ainda não foi criado
          // Vamos aguardar um momento e tentar novamente
          setTimeout(async () => {
            const { data: retryData, error: retryError } = await supabase
              .from('user_plans')
              .select('*')
              .eq('user_id', user.id)
              .eq('status', 'ativo')
              .limit(1)
              .single();

            if (!retryError && retryData) {
              setPlan(retryData as UserPlan);
            }
          }, 1000);
        }
      } catch (err: any) {
        console.error('Exception fetching user plan:', err);
        setError(err.message || 'Erro ao carregar plano');
      } finally {
        setLoading(false);
      }
    };

    fetchUserPlan();

    // Subscribe to changes in user_plans
    const channel = supabase
      .channel('user_plans_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_plans',
          filter: `user_id=eq.${user?.id}`
        },
        (payload) => {
          console.log('User plan changed:', payload);
          if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
            if (payload.new && (payload.new as any).status === 'ativo') {
              setPlan(payload.new as UserPlan);
            }
          }
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [user]);

  const refreshPlan = async () => {
    if (!user) return;

    try {
      const { data, error: rpcError } = await supabase.rpc('get_current_plan', {
        p_user_id: user.id
      });

      if (rpcError) {
        console.error('Error refreshing plan:', rpcError);
        setError(rpcError.message);
      } else if (data && data.length > 0) {
        setPlan(data[0] as UserPlan);
        setError(null);
      }
    } catch (err: any) {
      console.error('Exception refreshing plan:', err);
      setError(err.message);
    }
  };

  // Helper functions to check plan type
  const isFreePlan = plan?.plan_type === 'gratuito';
  const isBasicPlan = plan?.plan_type === 'basico';
  const isEssentialPlan = plan?.plan_type === 'essencial';
  const isEnterprisePlan = plan?.plan_type === 'empresarial';
  const isPaidPlan = plan?.plan_type !== 'gratuito';

  // Get plan display name
  const getPlanDisplayName = () => {
    if (!plan) return 'Sem plano';
    
    const names: Record<PlanType, string> = {
      gratuito: 'Gratuito',
      basico: 'Básico',
      essencial: 'Profissional',
      empresarial: 'Empresarial'
    };
    
    return names[plan.plan_type] || plan.plan_type;
  };

  return {
    plan,
    loading,
    error,
    refreshPlan,
    isFreePlan,
    isBasicPlan,
    isEssentialPlan,
    isEnterprisePlan,
    isPaidPlan,
    planDisplayName: getPlanDisplayName()
  };
};
